import { User } from "firebase/auth";
import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDelayedLoading } from "../hooks/useDelayedLoading";
import PageLoader from "../components/PageLoader";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { fetchLeague } from "../api/leagues";
import { submitPrediction } from "../api/predictions";
import { card, narrowPageContainer, primaryBtn, secondaryBtn, errorStyle , pageTitle } from "../styles";
import StatusDot from "../components/StatusDot";
import ModeIconSmall from "../components/league/ModeIconSmall";
import { MODE_CONTENT, MODE_COLOR_VAR } from "./createLeague/modeContent";
import { computePositionBands } from "../lib/fpBands";
import FpDragRow from "../components/league/FpDragRow";
import { useIsMobile } from "../hooks/useIsMobile";

interface Props {
  user: User;
}

interface Club {
  id: string;
  name: string;
  shortName: string | null;
  crest: string | null;
}

interface PredictionItem {
  clubId: string;
  club: Club;
  position: number;
}

export default function PredictionPage({ user }: Props) {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const [leagueName, setLeagueName] = useState("");
  const [leagueId, setLeagueId] = useState("");
  const [leagueStatus, setLeagueStatus] = useState("");
  const [leagueSeason, setLeagueSeason] = useState("");
  const [isLocked, setIsLocked] = useState(false);
  const [items, setItems] = useState<PredictionItem[]>([]);
  const [fpScoringConfig, setFpScoringConfig] = useState<{ topTierSize: number | null; relegationSize: number | null; championEnabled: boolean; topTierLabel: string | null; relegationLabel: string | null } | undefined>();
  const [loading, setLoading] = useState(true);
  const delayedLoading = useDelayedLoading(loading);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useBreadcrumbs(leagueName
    ? [{ label: t("common.home"), to: "/overview" }, { label: leagueName, to: `/leagues/${leagueId}` }, { label: t("prediction.title") }]
    : [{ label: t("common.home"), to: "/overview" }]
  );

  useEffect(() => {
    if (!id) return;
    fetchLeague(user, id)
      .then((data) => {
        setLeagueName(data.name);
        setLeagueId(data.id);
        setLeagueStatus(data.status ?? "");
        setLeagueSeason(data.competition?.season ?? "");
        const locked = data.isLocked ?? false;
        setIsLocked(locked);
        setFpScoringConfig(data.fpScoringConfig);

        const clubs = data.fpClubs ?? [];
        const existingPrediction = data.myPrediction ?? [];

        // Build ordered list: use existing prediction order if available, else alphabetical
        if (existingPrediction.length > 0) {
          const sorted = [...existingPrediction].sort((a, b) => a.position - b.position);
          setItems(sorted.map((p) => {
            const club = clubs.find((c) => c.id === p.clubId) ?? { id: p.clubId, name: p.clubId, shortName: null, crest: null };
            return { clubId: p.clubId, club, position: p.position };
          }));
        } else {
          const sorted = [...clubs].sort((a, b) => a.name.localeCompare(b.name));
          setItems(sorted.map((club, idx) => ({ clubId: club.id, club, position: idx + 1 })));
        }
      })
      .catch(() => setError(t("prediction.errors.failedToLoad")))
      .finally(() => setLoading(false));
  }, [id, user, t]);

  // ── Drag / jump-to-position state ──
  const dragIndex = useRef<number | null>(null);

  const moveItem = (from: number, to: number) => {
    if (to < 0 || to >= items.length) return;
    setItems((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next.map((item, idx) => ({ ...item, position: idx + 1 }));
    });
  };

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      await submitPrediction(user, id, items.map((item) => ({ clubId: item.clubId, position: item.position })));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      setError(t("prediction.errors.failedToSave"));
    } finally {
      setSaving(false);
    }
  };

  if (delayedLoading) return <PageLoader rows={3} />;
  if (loading) return null;

  if (isLocked) {
    return (
      <div style={{ ...narrowPageContainer, padding: "2rem 0" }}>
        <div style={card}>
          <h2>{t("prediction.locked.title")}</h2>
          <p>{t("prediction.locked.message")}</p>
          <Link to={`/leagues/${id}`} style={{ ...secondaryBtn, textDecoration: "none" }}>
            {t("prediction.locked.back")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="sg-fade-in" style={{ ...narrowPageContainer, padding: "1.5rem 0" }}>
      {/* Header card */}
      <div style={{ ...card, padding: 0, overflow: "hidden", marginBottom: "1.25rem" }}>
        <div style={{ padding: "1rem 1.5rem", borderBottom: "1px solid var(--color-border)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem", flexWrap: "wrap" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem" }}>
              <ModeIconSmall mode="FINAL_POSITIONS" color={MODE_COLOR_VAR.FINAL_POSITIONS} />
              <span style={{ fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: MODE_COLOR_VAR.FINAL_POSITIONS }}>
                {t(`${MODE_CONTENT.FINAL_POSITIONS.keyRoot}.label`)}
              </span>
            </span>
            <span style={{ color: "var(--color-border)", fontSize: "0.75rem" }}>·</span>
            {leagueSeason && (
              <>
                <span style={{ fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--color-text-dim)" }}>{leagueSeason}</span>
                <span style={{ color: "var(--color-border)", fontSize: "0.75rem" }}>·</span>
              </>
            )}
            {leagueStatus && <StatusDot status={leagueStatus} />}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <Link to={`/leagues/${id}`} style={{ ...secondaryBtn, textDecoration: "none", fontSize: "0.85rem" }}>
              ← {t("prediction.backToLeague")}
            </Link>
            <h1 style={pageTitle}>
              {t("prediction.title")}
            </h1>
          </div>
        </div>
        <div style={{ padding: "0.75rem 1.5rem" }}>
          <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
            {t("prediction.subtitle")}
          </p>
        </div>
      </div>

      {error && <p style={errorStyle}>{error}</p>}

      {/* Drag list */}
      <div style={{ ...card, padding: 0, overflow: "hidden", marginBottom: "1rem" }}>
        {(() => {
          const bands = computePositionBands({
            clubCount: items.length,
            topTierSize: fpScoringConfig?.topTierSize ?? null,
            relegationSize: fpScoringConfig?.relegationSize ?? null,
            championEnabled: fpScoringConfig?.championEnabled ?? true,
            topTierLabel: fpScoringConfig?.topTierLabel ?? null,
            relegationLabel: fpScoringConfig?.relegationLabel ?? null,
            fallbackLabels: { champion: t("prediction.bands.champion"), top: t("prediction.bands.top"), mid: t("prediction.bands.mid"), relegation: t("prediction.bands.relegation") },
          });
          return items.map((item, i) => (
            <FpDragRow
              key={item.clubId}
              index={i}
              total={items.length}
              label={item.club.shortName && item.club.shortName !== item.club.name ? item.club.shortName : item.club.name}
              band={bands[i]}
              dragIndexRef={dragIndex}
              move={moveItem}
              isLast={i === items.length - 1}
              isMobile={isMobile}
            />
          ));
        })()}
      </div>

      <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
        <button
          style={primaryBtn}
          onClick={handleSave}
          disabled={saving || items.length === 0}
        >
          {saving ? t("prediction.saving") : saved ? t("prediction.saved") : t("prediction.submit")}
        </button>
        <Link to={`/leagues/${id}`} style={{ ...secondaryBtn, textDecoration: "none" }}>
          {t("common.cancel")}
        </Link>
        {saved && (
          <span style={{ fontSize: "0.85rem", color: "var(--color-success)", fontWeight: 500 }}>✓ Saved</span>
        )}
      </div>
    </div>
  );
}
